import { Router } from 'vue-router';
import { email, helpers, required } from '@vuelidate/validators';
import { isValidNumber } from 'libphonenumber-js';

import { getColors } from '@/compositions';
import { ApiResponse, GroupAction, IAccountSetupStatus, IOptions, IStates, PractitionerType, StaffStatus } from '@/types';

export const apiResponse = <T>(response: ApiResponse<T>): ApiResponse<T> => {
  return response;
};

export const fallbackErrorResponse = (message = 'Error!') => ({
  message,
  data: [],
  success: false,
});

export const getAccountSetupRoute = (step: string | number | null | undefined): string => {
  const normalizedStep = String(step ?? "").trim().toLowerCase();

  if (!normalizedStep) return "/account/medical-center-data";
  if (normalizedStep === "1") return "/account/owner-licensed-info";
  if (normalizedStep === "2") return "/account/medical-center-docs";
  if (normalizedStep === "completed" || normalizedStep === "3") return "/";

  return "/account/medical-center-data";
};

export const isAccountSetupCompleted = (step: string | number | null | undefined): boolean => {
  const normalizedStep = String(step ?? "").trim().toLowerCase();
  return normalizedStep === "completed" || normalizedStep === "3";
};

const getStatusFlag = (status: IAccountSetupStatus | null | undefined, key: keyof IAccountSetupStatus, legacyKey: keyof IAccountSetupStatus): boolean => {
  return Boolean(status?.[key] ?? status?.[legacyKey]);
};

export const canAccessWorkspaceApp = (status: IAccountSetupStatus | null | undefined): boolean => {
  if (!status) {
    return false;
  }

  const step1Completed = getStatusFlag(status, "step1Completed", "Step1Completed");
  const setUpCompleted = getStatusFlag(status, "setUpCompleted", "SetUpCompleted");
  const workspaceReady = getStatusFlag(status, "workspaceReady", "WorkspaceReady");

  if (step1Completed || setUpCompleted || workspaceReady) {
    return true;
  }

  const normalizedStep = String(status.setUpStep ?? status.SetUpStep ?? "").trim().toLowerCase();

  return ["1", "2", "3", "completed"].includes(normalizedStep);
};

export const getPendingSetupSteps = (status: IAccountSetupStatus | null | undefined): string[] => {
  if (!status) {
    return ["Medical center data", "Owner licensed info", "Medical center docs"];
  }

  const step1Completed = getStatusFlag(status, "step1Completed", "Step1Completed");
  const step2Completed = getStatusFlag(status, "step2Completed", "Step2Completed");
  const step3Completed = getStatusFlag(status, "step3Completed", "Step3Completed");
  const setUpCompleted = getStatusFlag(status, "setUpCompleted", "SetUpCompleted");
  const workspaceReady = getStatusFlag(status, "workspaceReady", "WorkspaceReady");
  const hasExplicitStepFlags = step1Completed || step2Completed || step3Completed || setUpCompleted || workspaceReady;

  if (!hasExplicitStepFlags) {
    const normalizedStep = String(status.setUpStep ?? status.SetUpStep ?? "").trim().toLowerCase();

    if (!normalizedStep) {
      return ["Medical center data", "Owner licensed info", "Medical center docs"];
    }

    if (normalizedStep === "1") {
      return ["Owner licensed info", "Medical center docs"];
    }

    if (normalizedStep === "2") {
      return ["Medical center docs"];
    }

    return [];
  }

  if (!step1Completed) {
    return ["Medical center data", "Owner licensed info", "Medical center docs"];
  }

  if (!step2Completed && !step3Completed) {
    return ["Owner licensed info", "Medical center docs"];
  }

  if (!step2Completed && step3Completed) {
    return ["Owner licensed info"];
  }

  if (step2Completed && !step3Completed) {
    return ["Medical center docs"];
  }

  return [];
};

export const getNextAccountSetupRoute = (status: IAccountSetupStatus | null | undefined): string => {
  const pendingSteps = getPendingSetupSteps(status);

  if (!pendingSteps.length) return "/";
  if (pendingSteps[0] === "Medical center data") return "/account/medical-center-data";
  if (pendingSteps[0] === "Owner licensed info") return "/account/owner-licensed-info";
  if (pendingSteps[0] === "Medical center docs") return "/account/medical-center-docs";

  return "/account/medical-center-data";
};

export const setupCheck = (status: string | number | null | undefined | IAccountSetupStatus, router: Router) => {
  if (typeof status === "string" || typeof status === "number" || status == null) {
    router.push(getAccountSetupRoute(status));
    return;
  }

  router.push(getNextAccountSetupRoute(status));
};

export const dateConverter = (value: string | number | Date): Date => {
  return new Date(value);
};

export const onlyNumbers = ($event: KeyboardEvent): void => {
  const key = $event.key;
  const regex = /^\d+$/;

  if (!regex.test(key)) {
    $event.preventDefault();
  }
};

export const onlyNumbersAndSpecial = ($event: KeyboardEvent): void => {
  let key = $event.key;
  const regex = /^[0-9\/]/;

  if (!regex.test(key)) {
    $event.preventDefault();
  }
};

export const toTitleCase = (str: string): string => {
  const arr = str.split("_");
  const result: string[] = [];

  for (const word of arr) {
    result.push(word.charAt(0).toUpperCase() + word.slice(1));
  }

  return result.join(" ");
};

export const pasteOnlyNumbers = (e: ClipboardEvent): void => {
  e.preventDefault();
  e.stopPropagation();

  const pastedText = e.clipboardData?.getData('text') ?? '';
  const numbersOnly = pastedText.replace(/\D/g, '');

  const input = e.target as HTMLInputElement | null;
  if (!input) return;

  const start = input.selectionStart ?? 0;
  const end = input.selectionEnd ?? 0;

  const newValue =
    input.value.slice(0, start) +
    numbersOnly +
    input.value.slice(end);

  input.value = newValue;

  // notify Vue/other listeners of the change
  input.dispatchEvent(new Event('input', { bubbles: true }));
};


export const replaceSpaceWithHyphens = (value?: string): string | undefined => {
  return value?.replace(/[\s]+/g, "-");
};

export const removeTopLevelDomain = (value?: string): string | undefined => {
  return value?.replace(
    /(https?:\/\/)?(www\.)?[a-zA-Z0-9]+\.[a-zA-Z]{2,3}/g,
    ""
  );
};

export const allowOnlyDashAndAlphabet = (value?: string): string | undefined => {
  return value?.replace(/[^a-zA-Z-]/g, "");
};

export const formatMedicalCenterUrlName = (name?: string): string => {
  let formattedName = allowOnlyDashAndAlphabet(
    removeTopLevelDomain(
      replaceSpaceWithHyphens(name?.toLowerCase() ?? '')
    )
  );

  return formattedName ?? '';
};

export type CurrencyCode = 
  | "USD" | "EUR" | "GBP" | "JPY" | "AUD" 
  | "CAD" | "CHF" | "CNY" | "HKD" | "NZD" 
  | "NGN" | "CRC" | "KHR" | "BRL";

export const formatCurrency = (amount: number, currency: CurrencyCode): string => {
  const currencies: { [key in CurrencyCode]: string } = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    AUD: "A$",
    CAD: "C$",
    CHF: "Fr.",
    CNY: "¥",
    HKD: "HK$",
    NZD: "NZ$",
    NGN: "₦",
    CRC: "₡",
    KHR: "៛",
    BRL: "R$"
  };

  if (!(currency in currencies)) {
    return "";
  }
  
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

type MonthName = 
  | "January" | "February" | "March" | "April" | "May" | "June"
  | "July" | "August" | "September" | "October" | "November" | "December";

export const formatExpiryDate = (value: string): string => {
  const indexOfT = value.indexOf("T");
  // Replace hyphens with spaces and slice before 'T'
  const dateString = value.replace(/[-]+/g, " ").slice(0, indexOfT);
  const splitDate = dateString.split(" ");
  const year = parseInt(splitDate[0], 10);
  const months = parseInt(splitDate[1], 10);
  const day = parseInt(splitDate[2], 10);

  const monthsArray: { id: number; name: MonthName }[] = [
    { id: 1, name: "January" },
    { id: 2, name: "February" },
    { id: 3, name: "March" },
    { id: 4, name: "April" },
    { id: 5, name: "May" },
    { id: 6, name: "June" },
    { id: 7, name: "July" },
    { id: 8, name: "August" },
    { id: 9, name: "September" },
    { id: 10, name: "October" },
    { id: 11, name: "November" },
    { id: 12, name: "December" },
  ];

  // Find month name by id, fallback to empty string if not found
  const month = monthsArray.find(i => i.id === months)?.name || "";

  // Compose date string as "Month Day"
  const date = `${month} ${day}`;

  return date.replace(/[,]+/g, "");
};

export const shortMonthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

export const formatDate = (dateStr: string): string => {
  const dateObj = new Date(dateStr);
  const monthNames = shortMonthNames;
  const month = monthNames[dateObj.getMonth()];
  const day = dateObj.getDate().toString().padStart(2, "0");
  const year = dateObj.getFullYear().toString();
  return `${day} ${month} ${year}`;
};

export const formatDateWithWeekday = (date: Date): { day: string; weekday: string } => {
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const day = date.getDate();
  const weekday = weekdays[date.getDay()];

  return {
    day: day.toString().padStart(2, "0"),
    weekday: weekday,
  };
};

export const formatTimeOfTheDay = (hour: number): string => {
  if (hour >= 1 && hour < 6) {
    return "Night";
  } else if (hour >= 6 && hour < 12) {
    return "Morning";
  } else if (hour >= 12 && hour < 17) {
    return "Afternoon";
  } else if (hour >= 17 && hour < 22) {
    return "Evening";
  } else if (hour >= 22 && hour <= 23) {
    return "Night";
  } else {
    return "Night";
  }
};

export const checkIfItIsAUrl = (value: string): boolean => {
  return /^(ftp|http|https):\/\/[^ "]+$/.test(value);
};

export const sanitizeHTMLString = (unformattedHTML: string): string => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = unformattedHTML;
  return joiningHTMLStrings(tempDiv, "");
};

const joiningHTMLStrings = (element: Node, indent: string): string => {
  const html: string[] = [];

  for (let node = element.firstChild; node; node = node.nextSibling) {
    if (node.nodeType === 1) { // Element node
      const el = node as Element;
      html.push(indent + `<${el.tagName.toLowerCase()}>`);
      html.push(joiningHTMLStrings(node, indent + "  "));
      html.push(indent + `</${el.tagName.toLowerCase()}>`);
    } else if (node.nodeType === 3 && node.nodeValue?.trim() !== "") { // Text node
      html.push(indent + node.nodeValue?.trim());
    }
  }

  return html.join("\n");
};

export const validatePhonenumber = (value: string): boolean => {
  const allowedPair = ["70", "80", "81", "90", "91"];
  const secondAndThirdDigits = value.slice(5, 7);
  return allowedPair.includes(secondAndThirdDigits);
};

export const formatDateAndTime = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
  const year = date.getFullYear();
  const hours = date.getHours() % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const period = date.getHours() >= 12 ? 'pm' : 'am';

  return `${day} ${month} ${year}, ${hours}:${minutes}${period}`;
};

export const setOtpEndTime = () => {
  const expirationSeconds = 120;
  const endTime = Date.now() + expirationSeconds * 1000;
  localStorage.setItem('otpEndTime', endTime.toString());
};

export const isRemoteImage = (url: string | undefined | null): boolean => {
  if (!url) return false
  return /^https?:\/\//i.test(url) || /^http?:\/\//i.test(url)
};

export const isRequired = (fieldName = 'This field') =>
  helpers.withMessage(`${fieldName} is required`, required);

export const isEmail = helpers.withMessage(
  'Please enter a valid Email Address',
  email
);

export const isName = helpers.withMessage(
  'Only valid name characters are allowed',
  helpers.regex(/^[A-Za-zÀ-ÿ\s'-]+$/)
);

export const isNumber = helpers.withMessage(
  'Only numeric characters are allowed',
  helpers.regex(/^\d+$/)
);

export const isAddress = helpers.withMessage(
  'Only valid address characters are allowed',
  helpers.regex(/^[A-Za-z0-9À-ÿ\s.,#&'\/\-]+$/)
);

// Nigeria example (+234 logic)
export const isPhoneNumber = helpers.withMessage(
  'Phone number is invalid',
  (value: unknown) => {
    if (typeof value !== 'string' || !value.trim()) return false;

    const trimmed = value.trim();

    if (!isValidNumber(trimmed, 'NG')) return false;

    if (trimmed.startsWith('+234')) {
      // Nigerian numbers with +234 should be 17 chars (+234 000 000 0000)
      if (trimmed.length !== 17) return false;
    }

    // Add extra custom logic here if needed (e.g. block invalid prefixes)

    return true;
  }
);

/**
 * Validates that a date is today or before the given date.
 * @param referenceDate - ISO string or Date. Defaults to today.
 */
export const isPastDate = (referenceDate: string | Date = new Date()) =>
  helpers.withMessage('Date must not be in the future', (value: string) => {
    if (!value) return false;
    const compareTo = new Date(referenceDate);
    compareTo.setHours(0, 0, 0, 0);
    return new Date(value) <= compareTo;
  });

/**
 * Validates that a date is today or after the given date.
 * @param referenceDate - ISO string or Date. Defaults to today.
 */
export const isFutureDate = (referenceDate: string | Date = new Date()) =>
  helpers.withMessage('Date must not be in the past', (value: string) => {
    if (!value) return false;
    const compareTo = new Date(referenceDate);
    compareTo.setHours(0, 0, 0, 0);
    return new Date(value) >= compareTo;
  });

export const isUrl = helpers.withMessage(
  'Invalid URL: URL must be a valid pattern',
  (value: string) => {
    if (!helpers.req(value)) return true;

    if (/\s/.test(value)) return false;

    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }
);

export const isLinkedInUrl = helpers.withMessage(
  'Invalid LinkedIn URL',
  (value: string) => {
    if (!helpers.req(value)) return true;

    if (/\s/.test(value)) return false;

    try {
      new URL(value);
      const allowed = ["https://linkedin.com/in/", "https://www.linkedin.com/in/"];
      return allowed.some(prefix => value.startsWith(prefix));
    } catch {
      return false;
    }
  }
);

export const isTwitterUrl = helpers.withMessage(
  'Invalid Twitter/X URL',
  (value: string) => {
    if (!helpers.req(value)) return true;

    if (/\s/.test(value)) return false;

    try {
      new URL(value);
      const allowed = [
        "https://twitter.com/",
        "https://www.twitter.com/",
        "https://x.com/",
        "https://www.x.com/"
      ];
      return allowed.some(prefix => value.startsWith(prefix));
    } catch {
      return false;
    }
  }
);

export const isGroupName = helpers.withMessage(
  'Group name is invalid, special characters allowed: (! _ + - \' "). No two special characters or spaces in a row.',
  helpers.regex(/^(?!.*[!_+\-'" ]{2,})[A-Za-z0-9!_+\-'" ]+$/)
);

/**
 * Converts an array into an array of { value, label } objects.
 * 
 * @param data - Array of strings, numbers, or objects.
 * @param valueKey - Optional key to pick as value (for object arrays).
 * @param labelKey - Optional key to pick as label (for object arrays).
 * 
 * @returns Array of { value, label } objects.
 */
export const toLabelValue = <T>(
  data: T[],
  valueKey?: keyof T,
  labelKey?: keyof T
): IOptions[] =>
  data.map((item) => {
    if (typeof item === 'string' || typeof item === 'number') {
      return { value: String(item), label: String(item) };
    }

    if (valueKey && labelKey) {
      return {
        value: String(item[valueKey]),
        label: String(item[labelKey]),
      };
    }

    throw new Error('For object arrays, valueKey and labelKey must be provided');
  });

export const getCitiesByState = (selectedState: string, states: IStates[]): IOptions[] => {
  const selectedStateVrb = states.find(s => s.state === selectedState);

  if (!selectedStateVrb) return [];

  return toLabelValue(selectedStateVrb.city);
}

export const formatCamelCase = (str: string) => str.replace(/([a-z])([A-Z])/g, '$1 $2');

// Simple hash function
const hashCode = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

export const getColorByStaffId = (staffId: string | number): string => {
  const colors = getColors();
  const index = Math.abs(hashCode(String(staffId))) % colors.length;
  const colorVariable = colors[index] ?? '--ch-blue-100';

  return `var(${colorVariable})`;
};

export const transformPractitionerType = (practitionerType: PractitionerType) => practitionerType === PractitionerType.NON_MEDICAL_PRACTITIONER ? 'Non-Medical' : 'Medical';

export const isResendInviteValid = (status: StaffStatus): boolean => {
  return ([StaffStatus.INACTIVE, StaffStatus.PENDING, StaffStatus.REJECTED, StaffStatus.EXPIRED, StaffStatus.ONHOLD].includes(status));
};

export const getPermissionAction = (permissionName: string): GroupAction => {
  return permissionName.split('::').pop() as GroupAction;
};

export const buildQueryParams = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    const isValid =
      value !== undefined &&
      value !== null &&
      value !== '' &&
      value !== 'NaN-NaN-NaN' &&
      !Number.isNaN(value) &&
      typeof value !== 'function' &&
      typeof value !== 'symbol';

    if (isValid) {
      const stringifiedValue =
        typeof value === 'object' && !Array.isArray(value) ? JSON.stringify(value) : String(value);

      searchParams.set(key, stringifiedValue);
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

export const formatQuerySpace = (query: string): string => {
  return query.trim().replace(/\s+/g, '&');
};
